/**
 * Resolves the organization a cart or order is attributed to.
 *
 * This must stay the single source of truth: `createCart`, `completeCart` and
 * the approval sync all have to agree on which organization is active. When
 * they disagree, approval can be resolved for one organization while the order
 * is placed on another — which is how an unapproved organization was able to
 * purchase a restricted product.
 *
 * A Clerk organization (`orgSlug`, which is the organization number) wins over
 * the Feide school, because a user can be both a Feide user and a member of an
 * invited Clerk organization at the same time.
 *
 * `orgSlug` may be passed separately for server callers that read it from
 * `auth()`; it falls back to the session claim when not supplied.
 */
export const getActiveOrganizationNumber = (
  sessionClaims: CustomJwtSessionClaims | null | undefined,
  orgSlug?: string | null,
): string | undefined =>
  orgSlug ||
  sessionClaims?.orgSlug ||
  sessionClaims?.publicMeta?.activeSchool ||
  undefined;

/**
 * Whether the currently active organization is approved to purchase restricted
 * products.
 *
 * The stored approval is only honoured when it was resolved for the very same
 * organization that is active now. `activeSchoolApproved` on its own is a stale
 * global flag: a Feide user whose school is approved would otherwise carry that
 * approval over to an unapproved Clerk organization they were invited to.
 *
 * This is a UI-level gate only. The authoritative check is the server-side
 * guard, which reads `schoolCustomerStatus` live from Crystallize.
 */
export const isActiveOrganizationApproved = (
  sessionClaims: CustomJwtSessionClaims | null | undefined,
): boolean => {
  const orgNumber = getActiveOrganizationNumber(sessionClaims);
  if (!orgNumber) return false;

  return (
    sessionClaims?.publicMeta?.activeSchoolApproved === true &&
    sessionClaims?.publicMeta?.approvedOrgNumber === orgNumber
  );
};

/**
 * Whether the stored approval was resolved against the organization that is
 * active now. A mismatch means the claim is stale (legacy metadata without
 * `approvedOrgNumber`, or a just-switched organization) and has to be
 * re-resolved before it can be trusted.
 */
export const isApprovalStale = (
  sessionClaims: CustomJwtSessionClaims | null | undefined,
): boolean => {
  const orgNumber = getActiveOrganizationNumber(sessionClaims);
  if (!orgNumber) return false;

  return sessionClaims?.publicMeta?.approvedOrgNumber !== orgNumber;
};
